import { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { useAuthStore } from '@/stores/authStore';
import type { TenantConfig } from '@shared/types/tenant-config';

interface TimezoneContextType {
  timezone: string;
  isLoading: boolean;
  error: string | null;
  publicConfig: { branding?: TenantConfig['branding']; terminology?: TenantConfig['terminology'] } | null;
}

const TimezoneContext = createContext<TimezoneContextType>({
  timezone: 'America/Denver', // Default fallback
  isLoading: true,
  error: null,
  publicConfig: null,
});

/**
 * Auth-aware TimezoneProvider
 * - Pre-login: Fetches timezone from /api/tenant/public
 * - Post-login: Uses timezone from authStore (no API call!)
 */
export const TimezoneProvider = ({ children }: { children: ReactNode }) => {
  const [timezone, setTimezone] = useState<string>('America/Denver');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [publicConfig, setPublicConfig] = useState<{
    branding?: TenantConfig['branding'];
    terminology?: TenantConfig['terminology'];
  } | null>(null);

  // Get auth state from store
  const { tenant, isLoading: authLoading } = useAuthStore();

  useEffect(() => {
    // Wait for auth to initialize
    if (authLoading) {
      return;
    }

    // Use tenant from authStore (works for both authenticated and unauthenticated users)
    // authStore now fetches public config for unauthenticated users, so we always have tenant data
    if (tenant?.timezone) {
      setTimezone(tenant.timezone);
      // Also provide branding/terminology from tenant settings for consistency
      setPublicConfig({
        branding: tenant.settings.branding,
        terminology: tenant.settings.terminology,
      });
      setIsLoading(false);
      setError(null);
    } else {
      // No tenant available (e.g., portal site) - use defaults
      setTimezone('America/Denver');
      setPublicConfig(null);
      setIsLoading(false);
      setError(null);
    }
  }, [tenant, authLoading]); // Re-run when auth state changes

  return (
    <TimezoneContext.Provider value={{ timezone, isLoading, error, publicConfig }}>{children}</TimezoneContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useSchoolTimezone = () => {
  const context = useContext(TimezoneContext);
  if (!context) {
    throw new Error('useSchoolTimezone must be used within TimezoneProvider');
  }
  return context;
};
